import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, Spin, message, Button, Modal, Tooltip, Space } from 'antd';
import { FilePdfOutlined, ReloadOutlined, FullscreenOutlined, FullscreenExitOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const MiniPrinter = ({ configEndpoint, generateEndpoint, data }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [useIframe, setUseIframe] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Clean up PDF URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Auto-generate PDF when component mounts
  useEffect(() => {
    if (data) {
      generatePDF();
    }
  }, [data]);

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setPdfUrl(null);
      setError(null);
      setLoading(false);
    }
  }, [isModalOpen]);

  const generatePDF = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: configData } = await axios.get(configEndpoint, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });

      const request = {
        ...configData.content,
        data: {
          ...configData.content.data,
          ...data
        }
      };

      const pdfResponse = await axios.post(generateEndpoint, request, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        },
        responseType: 'blob',
      });

      const contentType = pdfResponse.headers['content-type'];
      if (contentType !== 'application/pdf') {
        throw new Error(t('Received non-PDF response: {{contentType}}', { contentType }));
      }

      const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    } catch (error) {
      console.error(t('Error fetching config or generating PDF:'), error);
      setError(error.message || t('Failed to generate PDF'));
      message.error(t('Failed to generate PDF'));
    } finally {
      setLoading(false);
    }
  };

  const toggleViewerType = () => {
    setUseIframe(!useIframe);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Clean up PDF URL when modal closes
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const renderPDFViewer = () => {
    const viewerStyle = isFullscreen
      ? { width: '100%', height: 'calc(100vh - 64px)' }
      : { width: '100%', height: '300px' }; // Limited height for inline display

    return (
      <iframe
        src={pdfUrl}
        style={viewerStyle}
        title={t('PDF Viewer')}
      />
    );
  };

  return (
    <>
      <div style={{ 
        border: '1px solid #d9d9d9', 
        borderRadius: 8, 
        padding: 16,
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <Spin size="large" />
            <span>{t('Generating PDF...')}</span>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'red', marginBottom: 16 }}>{t('Error')}: {error}</p>
            <Button onClick={generatePDF}>{t('Try Again')}</Button>
          </div>
        ) : pdfUrl ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{t('PDF Generated Successfully')}</span>
              <Space>
                <Tooltip title={useIframe ? t('Switch to PDFViewer') : t('Switch to Browser PDF Viewer')}>
                  <Button 
                    size="small" 
                    icon={useIframe ? <EyeOutlined /> : <EyeInvisibleOutlined />} 
                    onClick={toggleViewerType} 
                  />
                </Tooltip>
                <Tooltip title={isFullscreen ? t('Exit Fullscreen') : t('Fullscreen')}>
                  <Button 
                    size="small" 
                    icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
                    onClick={toggleFullscreen} 
                  />
                </Tooltip>
              </Space>
            </div>
            {renderPDFViewer()}
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <FilePdfOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: 16 }} />
            <p style={{ marginBottom: 16, fontSize: '14px' }}>{t('Preparing PDF agreement...')}</p>
            <Button 
              type="primary" 
              icon={<FilePdfOutlined />} 
              onClick={generatePDF}
              size="large"
            >
              {t('Generate PDF')}
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        destroyOnHidden
        title={
          <Space>
            <FilePdfOutlined />
            {t('PDF Agreement')}
          </Space>
        }
      >
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px' 
          }}>
            <Spin size="large" tip={t('Generating PDF...')} />
          </div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: 'red' }}>{t('Error')}: {error}</p>
            <Button onClick={generatePDF}>{t('Try Again')}</Button>
          </div>
        ) : pdfUrl ? (
          <div style={{ width: '100%' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: 16 
            }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{t('PDF Agreement')}</span>
              <Space>
                <Tooltip title={useIframe ? t('Switch to PDFViewer') : t('Switch to Browser PDF Viewer')}>
                  <Button 
                    icon={useIframe ? <EyeOutlined /> : <EyeInvisibleOutlined />} 
                    onClick={toggleViewerType} 
                  />
                </Tooltip>
                <Tooltip title={isFullscreen ? t('Exit Fullscreen') : t('Fullscreen')}>
                  <Button 
                    icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
                    onClick={toggleFullscreen} 
                  />
                </Tooltip>
              </Space>
            </div>
            {renderPDFViewer()}
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px' 
          }}>
            <Button 
              type="primary" 
              icon={<FilePdfOutlined />} 
              onClick={generatePDF}
              size="large"
            >
              {t('Generate PDF')}
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default MiniPrinter;
